import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import '../styles/Navbar.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;

    i18n.changeLanguage(newLanguage);
    if (newLanguage === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }

    try {
      localStorage.setItem('language', newLanguage);
    } catch (error) {
      // Ignore local storage errors in restricted browser modes
    }

    try {
      await authAPI.updateProfile({ preferred_language: newLanguage });
      if (updateUser) {
        updateUser({ preferred_language: newLanguage });
      }
    } catch (error) {
      // Ignore profile update failures, language already changed locally
    }
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  // Check user roles
  const isOwner = user?.user_role === 'owner';
  const isSeller = user?.user_role === 'seller';
  const isAdmin = user?.user_role === 'superadmin' || user?.user_role === 'admin';

  // Get role display name
  const getRoleName = () => {
    if (isAdmin) return t('admin') || 'Admin';
    if (isOwner) return 'Owner';
    if (isSeller) return 'Seller';
    return user?.user_role || 'User';
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-brand">
          <h2>{isAdmin ? t('adminPanel') || 'Admin Panel' : (user?.market_name || 'POS System')}</h2>
        </div>

        <ul className="navbar-menu">
          {isAdmin && (
            <>
              <li>
                <Link to="/admin-panel" className={isActive('/admin-panel')}>
                  ⚙️ {t('adminPanel') || 'Admin Panel'}
                </Link>
              </li>
              <li>
                <Link to="/manage-users" className={isActive('/manage-users')}>
                  👥 {t('manageUsers') || 'إدارة المستخدمين'}
                </Link>
              </li>
            </>
          )}
          
          {isOwner && (
            <>
              <li>
                <Link to="/dashboard" className={isActive('/dashboard')}>
                  {t('dashboard')}
                </Link>
              </li>
              <li>
                <Link to="/items" className={isActive('/items')}>
                  {t('items')}
                </Link>
              </li>
              <li>
                <Link to="/categories-brands" className={isActive('/categories-brands')}>
                  {t('categoriesAndBrands')}
                </Link>
              </li>
              <li>
                <Link to="/reports" className={isActive('/reports')}>
                  {t('reports')}
                </Link>
              </li>
              <li>
                <Link to="/receipts" className={isActive('/receipts')}>
                  {t('receipts')}
                </Link>
              </li>
              <li>
                <Link to="/debtors" className={isActive('/debtors')}>
                  {t('debtors')}
                </Link>
              </li>
              <li>
                <Link to="/pos" className={isActive('/pos')}>
                  {t('pos')}
                </Link>
              </li>
            </>
          )}
          
          {isSeller && (
            <li>
              <Link to="/pos" className={isActive('/pos')}>
                {t('pos')}
              </Link>
            </li>
          )}
        </ul>

        <div className="navbar-user">
          <select
            value={i18n.language}
            onChange={handleLanguageChange}
            className="language-selector"
            title={t('selectLanguage') || 'Select Language'}
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="fr">Français</option>
          </select>
          <span className="user-name">
            {user?.username} ({getRoleName()})
          </span>
          <button onClick={handleLogout} className="logout-btn">
            {t('logout')}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
